import { css } from '@emotion/react';
import { theme } from '@Guidelines/Theme/theme';
import { fieldWithoutMarginStyles } from '@Guidelines/Theme/commonPassthrough';
import type { BeneficiaryAgencyScope } from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/BeneficiaryAgencyScope';
import type { AgencyScopeOption } from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/Helpers/buildAgencyScopeOptions';
import resolveAgencyScopeKey from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/Helpers/resolveAgencyScopeKey';
import beneficiaryFiltersSignal, {
	setBeneficiaryFilters,
} from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/Signals/beneficiaryFiltersSignal';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { Dropdown } from '@verclam/ui-kit';

const AGENCY_SCOPE_FILTER_THRESHOLD = 8;
const DEPTH_INDENT_IN_REM = 1;

const createAgencyScopeOptionStyle = (depth: number, isStructure: boolean) => css`
	padding-left: ${depth * DEPTH_INDENT_IN_REM}rem;
	font-weight: ${isStructure ? theme.fontWeights.semibold : theme.fontWeights.medium};
`;

const isAgencyScopeOption = (option: unknown): option is AgencyScopeOption =>
	typeof option === 'object' && option !== null && 'scope' in option && 'depth' in option;

type Props = {
	readonly options: readonly AgencyScopeOption[];
	readonly currentUserId: number;
	readonly withLabel?: boolean;
};

/**
 * The agencies of the caller, grouped by structure. Narrowing the scope drops a referent picked from
 * another agency's directory, but keeps « Mes dossiers ».
 */
const AgencyScopeFilter: React.FC<Props> = ({ options, currentUserId, withLabel = false }) => {
	const currentFilters = useSignalValue(beneficiaryFiltersSignal);
	const agencyScopeKey = resolveAgencyScopeKey(currentFilters.agencyScope);

	const handleAgencyScopeChange = React.useCallback(
		(key: string | null) => {
			const option = options.find((candidate) => candidate.key === key);
			const scope: BeneficiaryAgencyScope = option?.scope ?? { type: 'all' };
			const facilityIds = option?.facilityIds ?? [];
			const filters = beneficiaryFiltersSignal.peek();
			const scoped = filters.setAgencyScope(scope, facilityIds);
			const keepsAssignedUser =
				filters.assignedUserId === null || filters.assignedUserId === currentUserId;

			setBeneficiaryFilters(keepsAssignedUser ? scoped : scoped.setAssignedUserId(null));
		},
		[options, currentUserId],
	);

	const renderAgencyScopeOption = React.useCallback((option: unknown) => {
		if (!isAgencyScopeOption(option)) {
			return null;
		}

		const { label, depth, scope } = option;

		return <span css={createAgencyScopeOptionStyle(depth, scope.type === 'organization')}>{label}</span>;
	}, []);

	return (
		<Dropdown<string>
			name="beneficiaryAgencyScope"
			label="Agence"
			isLabelHidden={!withLabel}
			options={options}
			optionLabel="label"
			optionValue="key"
			value={agencyScopeKey}
			onChange={handleAgencyScopeChange}
			itemTemplate={renderAgencyScopeOption}
			filter={options.length > AGENCY_SCOPE_FILTER_THRESHOLD}
			pt={fieldWithoutMarginStyles}
		/>
	);
};

export default AgencyScopeFilter;
